import type { PocInputValidity } from 'commons/types';
import { useTranslation } from 'core/i18n/i18n';
import React, { useEffect } from 'react';
import { ConnectivityProperties } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.model';
import { INVALID_STATUS_TRACKING_STATE, VALID_STATUS_TRACKING_STATE } from '../../constants';

type ConnectivityStatusTrackingCardValidation = 'warning-less-than-alert' | 'value-must-be-positive';

interface UseConnectivityStatusTrackingCardValidationProps {
    onValidityChange?: (isValid: PocInputValidity) => void;
}

export const useConnectivityStatusTrackingCardValidation = ({
    onValidityChange,
}: UseConnectivityStatusTrackingCardValidationProps) => {
    const [validity, setValidity] = React.useState<PocInputValidity>(VALID_STATUS_TRACKING_STATE);
    const [internalCustomValidation, setInternalCustomValidation] =
        React.useState<ConnectivityStatusTrackingCardValidation>();
    const { t } = useTranslation();

    const checkValidation = (values: ConnectivityProperties): PocInputValidity => {
        if (values.warningHours < 0 || values.alertHours < 0) {
            setInternalCustomValidation('value-must-be-positive');
            setValidity(INVALID_STATUS_TRACKING_STATE);
            return INVALID_STATUS_TRACKING_STATE;
        }

        if (values.warningHours > values.alertHours) {
            setInternalCustomValidation('warning-less-than-alert');
            setValidity(INVALID_STATUS_TRACKING_STATE);
            return INVALID_STATUS_TRACKING_STATE;
        }

        setInternalCustomValidation(undefined);
        setValidity(VALID_STATUS_TRACKING_STATE);
        return VALID_STATUS_TRACKING_STATE;
    };

    const getValidationMessageByType = React.useCallback(() => {
        const validationMessageByType = {
            'warning-less-than-alert': t(
                'settings.devices.device-settings-configuration-status-tracking.connectivity.validity.alertLessThanWarning'
            ),
            'value-must-be-positive': t(
                'settings.devices.device-settings-configuration-status-tracking.connectivity.validity.mustBePositive'
            ),
        };
        return validationMessageByType[internalCustomValidation!] || '';
    }, [t, internalCustomValidation]);

    useEffect(() => {
        onValidityChange?.(validity);
    }, [validity]);

    return { validity, getValidationMessageByType, checkValidation };
};
